import { useEffect, useRef, useState, type FormEvent } from 'react'
import { logIn } from './api'

const RIJEN = [
  ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
  ['!', '@', '#', '?', '-', '_', '.', '&', '*', '$'],
]

/**
 * Een toetsenbord op het scherm: de kiosk heeft er geen, en of Windows in de
 * kioskstand zijn eigen schermtoetsenbord laat zien, is niet zeker. Shift
 * geldt voor één letter, zoals op een telefoon.
 */
function Toetsenbord({ onToets, onWis, onKlaar, uit }: {
  onToets: (t: string) => void
  onWis: () => void
  onKlaar: () => void
  uit: boolean
}) {
  const [hoofd, setHoofd] = useState(false)
  const druk = (t: string) => {
    onToets(hoofd ? t.toUpperCase() : t)
    setHoofd(false)
  }
  // `onPointerDown` + preventDefault: het invoerveld houdt de focus, en een
  // tik voelt direct in plaats van na de klikvertraging.
  const knop = (inhoud: string, actie: () => void, klasse = '', label?: string) => (
    <button
      type="button"
      className={`beh-toets ${klasse}`}
      aria-label={label}
      onPointerDown={(e) => { e.preventDefault(); actie() }}
      disabled={uit}
    >
      {inhoud}
    </button>
  )
  return (
    <div className="beh-toetsenbord" aria-label="Toetsenbord">
      {RIJEN.map((rij, i) => (
        <div className="beh-rij" key={i}>
          {i === 3 && knop('⇧', () => setHoofd((h) => !h), `beh-breed${hoofd ? ' beh-aan' : ''}`, 'Hoofdletter')}
          {rij.map((t) => (
            <span key={t} className="beh-toets-plek">
              {knop(hoofd ? t.toUpperCase() : t, () => druk(t))}
            </span>
          ))}
          {i === 3 && knop('⌫', onWis, 'beh-breed', 'Wis')}
        </div>
      ))}
      <div className="beh-rij">
        {knop('spatie', () => onToets(' '), 'beh-spatie')}
        {knop('Inloggen', onKlaar, 'beh-klaar')}
      </div>
    </div>
  )
}

const tijd = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/**
 * Het inlogscherm van het beheer (docs/beheer.md). De Worker beslist: het
 * wachtwoord staat niet in de app, en na vijf mislukte pogingen binnen tien
 * minuten weigert hij dit adres tot die tien minuten om zijn.
 */
export function Inloggen({ onIngelogd, toetsenbord = false }: {
  onIngelogd: (token: string) => void
  /** In de kiosk: een toetsenbord op het scherm. */
  toetsenbord?: boolean
}) {
  const [wachtwoord, setWachtwoord] = useState('')
  const [zichtbaar, setZichtbaar] = useState(false)
  const [bezig, setBezig] = useState(false)
  const [melding, setMelding] = useState<string | null>(null)
  const [geblokkeerdTot, setGeblokkeerdTot] = useState<number | null>(null)
  const [nu, setNu] = useState(Date.now())
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (geblokkeerdTot === null) return
    const tik = window.setInterval(() => {
      const n = Date.now()
      setNu(n)
      if (n >= geblokkeerdTot) { setGeblokkeerdTot(null); setMelding(null) }
    }, 1000)
    return () => window.clearInterval(tik)
  }, [geblokkeerdTot])

  const rest = geblokkeerdTot === null ? 0 : Math.max(0, Math.ceil((geblokkeerdTot - nu) / 1000))
  const geblokkeerd = rest > 0

  const stuur = async (e?: FormEvent) => {
    e?.preventDefault()
    if (bezig || geblokkeerd || !wachtwoord) return
    setBezig(true)
    setMelding(null)
    const uitkomst = await logIn(wachtwoord)
    setBezig(false)
    setWachtwoord('')
    if (uitkomst.ok) { onIngelogd(uitkomst.token); return }
    switch (uitkomst.reden) {
      case 'onjuist':
        setMelding(`Onjuist wachtwoord. Nog ${uitkomst.over} ${uitkomst.over === 1 ? 'poging' : 'pogingen'} binnen tien minuten.`)
        break
      case 'geblokkeerd':
        setNu(Date.now())
        setGeblokkeerdTot(Date.now() + uitkomst.wacht * 1000)
        setMelding('Te veel pogingen.')
        break
      case 'verbinding':
        setMelding('Geen verbinding met de server: inloggen kan nu niet.')
        break
      default:
        setMelding('Inloggen lukte niet. Probeer het zo opnieuw.')
    }
    veld.current?.focus()
  }

  return (
    <form className="beh-inloggen" onSubmit={stuur}>
      <label className="beh-label" htmlFor="beh-wachtwoord">Wachtwoord</label>
      <div className="beh-veldrij">
        <input
          id="beh-wachtwoord"
          ref={veld}
          className="beh-veld"
          type={zichtbaar ? 'text' : 'password'}
          value={wachtwoord}
          onChange={(e) => setWachtwoord(e.target.value)}
          autoComplete="current-password"
          autoFocus={!toetsenbord}
          // Op de kiosk geen toetsenbord van het systeem over het onze heen.
          inputMode={toetsenbord ? 'none' : undefined}
          disabled={geblokkeerd}
          maxLength={200}
        />
        <button type="button" className="beh-oog" onClick={() => setZichtbaar((z) => !z)}>
          {zichtbaar ? 'Verberg' : 'Toon'}
        </button>
      </div>
      <p className={`beh-melding${melding ? ' beh-fout' : ''}`} role="status" aria-live="polite">
        {geblokkeerd
          ? `Te veel pogingen. Probeer het over ${tijd(rest)} opnieuw.`
          : melding ?? 'Maximaal vijf pogingen per tien minuten.'}
      </p>
      {!toetsenbord && (
        <button type="submit" className="beh-knop" disabled={bezig || geblokkeerd || !wachtwoord}>
          {bezig ? 'Bezig…' : 'Inloggen'}
        </button>
      )}
      {toetsenbord && (
        <Toetsenbord
          onToets={(t) => setWachtwoord((w) => (w + t).slice(0, 200))}
          onWis={() => setWachtwoord((w) => w.slice(0, -1))}
          onKlaar={() => void stuur()}
          uit={bezig || geblokkeerd}
        />
      )}
    </form>
  )
}
